import { useCallback, useEffect, useRef, useState } from "react";

import {
  useCreateDocumentFromRecordingMutation,
  useCreateDocumentUploadMutation,
  useDocumentStatusLazyQuery,
  useFinalizeDocumentUploadMutation,
  useRemoveAiInterviewerDocumentMutation,
} from "../../../graphql";

export type DocumentAnalysis = {
  category: string;
  documentType: string;
  quality: number;
  summary?: string;
  critique?: string;
};

export type DocumentUploadPhase =
  | "idle"
  | "uploading"
  | "processing"
  | "reviewing"
  | "complete"
  | "error";

export type DocumentUploadState = {
  id: string;
  phase: DocumentUploadPhase;
  fileName: string;
  fileMimeType: string;
  fileSizeBytes: number;
  uploadProgress: number;
  documentId: string | null;
  analysis: DocumentAnalysis | null;
  errorMessage: string | null;
};

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 120;

const EXTENSION_MIME_MAP: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  text: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  html: "text/html",
  htm: "text/html",
  rtf: "application/rtf",
};

function inferMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_MIME_MAP[ext] ?? "application/octet-stream";
}

function parseAnalysisFromExtra(
  extra: Record<string, unknown> | null | undefined
): DocumentAnalysis | null {
  if (!extra) return null;
  const analysis = extra.ai_analysis as Record<string, unknown> | undefined;
  if (!analysis) return null;
  const rawCategory = (analysis.document_category as string) || "";
  const category = rawCategory.toUpperCase() || "UNKNOWN";

  const rawType = (analysis.document_type as string) || "";
  const documentType = rawType.toUpperCase() || "UNKNOWN";

  const rawQuality = analysis.document_quality;
  const quality = typeof rawQuality === "number" ? rawQuality / 100 : 0;

  return {
    category,
    documentType,
    quality,
    summary: (analysis.short_description as string) || undefined,
    critique: (analysis.short_critique as string) || undefined,
  };
}

type UseDocumentUploadsParams = {
  aiInterviewerId: string | null;
  onUploadComplete?: (upload: DocumentUploadState) => void;
};

type UseDocumentUploadsResult = {
  uploads: DocumentUploadState[];
  startUpload: (file: File) => string;
  startRecordingUpload: (
    callId: string,
    callName: string,
    positionTitle?: string | null
  ) => string;
  removeUpload: (uploadId: string) => void;
};

export function useDocumentUploads({
  aiInterviewerId,
  onUploadComplete,
}: UseDocumentUploadsParams): UseDocumentUploadsResult {
  const [uploads, setUploads] = useState<DocumentUploadState[]>([]);
  const xhrsRef = useRef<Map<string, XMLHttpRequest>>(new Map());
  const pollTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const pollCountsRef = useRef<Map<string, number>>(new Map());
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  const pendingRecordingsRef = useRef<
    Map<
      string,
      { callId: string; callName: string; positionTitle?: string | null }
    >
  >(new Map());

  const [createUpload] = useCreateDocumentUploadMutation();
  const [finalizeUpload] = useFinalizeDocumentUploadMutation();
  const [fetchDocumentStatus] = useDocumentStatusLazyQuery({
    fetchPolicy: "network-only",
  });
  const [unlinkDocument] = useRemoveAiInterviewerDocumentMutation();
  const [createFromRecording] = useCreateDocumentFromRecordingMutation();

  const updateUpload = useCallback(
    (id: string, patch: Partial<DocumentUploadState>) => {
      setUploads((prev) =>
        prev.map((u) => (u.id === id ? { ...u, ...patch } : u))
      );
    },
    []
  );

  const cleanupUpload = useCallback((id: string) => {
    const xhr = xhrsRef.current.get(id);
    if (xhr) {
      xhr.abort();
      xhrsRef.current.delete(id);
    }
    const timer = pollTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      pollTimersRef.current.delete(id);
    }
    pollCountsRef.current.delete(id);
    pendingFilesRef.current.delete(id);
    pendingRecordingsRef.current.delete(id);
  }, []);

  const removeUpload = useCallback(
    (uploadId: string) => {
      const upload = uploads.find((u) => u.id === uploadId);
      cleanupUpload(uploadId);
      setUploads((prev) => prev.filter((u) => u.id !== uploadId));

      if (aiInterviewerId && upload?.documentId) {
        unlinkDocument({
          variables: {
            aiInterviewerId,
            documentId: upload.documentId,
          },
        });
      }
    },
    [cleanupUpload, uploads, aiInterviewerId, unlinkDocument]
  );

  const pollForStatus = useCallback(
    (uploadId: string, documentId: string) => {
      const count = (pollCountsRef.current.get(uploadId) || 0) + 1;
      pollCountsRef.current.set(uploadId, count);

      if (count > MAX_POLL_ATTEMPTS) {
        updateUpload(uploadId, {
          phase: "error",
          errorMessage: "Document processing timed out",
        });
        return;
      }

      fetchDocumentStatus({ variables: { documentId } }).then(({ data }) => {
        const status = data?.documentStatus;
        if (!status) {
          pollTimersRef.current.set(
            uploadId,
            setTimeout(
              () => pollForStatus(uploadId, documentId),
              POLL_INTERVAL_MS
            )
          );
          return;
        }

        const statusUpper = status.status.toUpperCase();

        if (statusUpper === "ERROR") {
          updateUpload(uploadId, {
            phase: "error",
            errorMessage: status.errorMessage || "Document processing failed",
          });
          return;
        }

        if (statusUpper === "READY") {
          let extra: Record<string, unknown> | null = null;
          if (status.extra) {
            try {
              extra =
                typeof status.extra === "string"
                  ? (JSON.parse(status.extra) as Record<string, unknown>)
                  : (status.extra as Record<string, unknown>);
            } catch {
              extra = null;
            }
          }
          const analysis = parseAnalysisFromExtra(extra);

          setUploads((prev) => {
            const updated = prev.map((u) =>
              u.id === uploadId
                ? {
                    ...u,
                    phase: "complete" as const,
                    analysis,
                    uploadProgress: 1,
                  }
                : u
            );
            const completed = updated.find((u) => u.id === uploadId);
            if (completed) {
              onUploadComplete?.(completed);
            }
            return updated;
          });
          return;
        }

        const phase: DocumentUploadPhase =
          statusUpper === "PROCESSING" ? "reviewing" : "processing";
        updateUpload(uploadId, { phase });

        pollTimersRef.current.set(
          uploadId,
          setTimeout(
            () => pollForStatus(uploadId, documentId),
            POLL_INTERVAL_MS
          )
        );
      });
    },
    [fetchDocumentStatus, updateUpload, onUploadComplete]
  );

  const executeUpload = useCallback(
    (uploadId: string, file: File, interviewerId: string) => {
      const fileName = file.name;
      const fileMimeType = inferMimeType(file);

      createUpload({
        variables: { aiInterviewerId: interviewerId, fileName, fileMimeType },
      })
        .then(({ data, errors }) => {
          if (errors?.length || !data?.createDocumentUpload) {
            throw new Error(
              errors?.[0]?.message || "Failed to create upload URL"
            );
          }

          const { uploadUrl, blobName } = data.createDocumentUpload;
          const xhr = new XMLHttpRequest();
          xhrsRef.current.set(uploadId, xhr);

          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              updateUpload(uploadId, {
                uploadProgress: e.loaded / e.total,
              });
            }
          });

          xhr.addEventListener("error", () => {
            xhrsRef.current.delete(uploadId);
            updateUpload(uploadId, {
              phase: "error",
              errorMessage: "Upload failed. Please try again.",
            });
          });

          xhr.addEventListener("abort", () => {
            xhrsRef.current.delete(uploadId);
          });

          xhr.addEventListener("load", () => {
            xhrsRef.current.delete(uploadId);

            if (xhr.status < 200 || xhr.status >= 300) {
              updateUpload(uploadId, {
                phase: "error",
                errorMessage: `Upload failed with status ${xhr.status}`,
              });
              return;
            }

            updateUpload(uploadId, {
              phase: "processing",
              uploadProgress: 1,
            });

            finalizeUpload({
              variables: {
                aiInterviewerId: interviewerId,
                blobName,
                fileName,
                fileMimeType,
              },
            })
              .then(({ data: finalizeData, errors: finalizeErrors }) => {
                if (
                  finalizeErrors?.length ||
                  !finalizeData?.finalizeDocumentUpload?.document
                ) {
                  throw new Error(
                    finalizeErrors?.[0]?.message || "Failed to finalize upload"
                  );
                }

                const docId = finalizeData.finalizeDocumentUpload.document.id;
                updateUpload(uploadId, {
                  documentId: docId,
                  phase: "processing",
                });

                pollCountsRef.current.set(uploadId, 0);
                pollForStatus(uploadId, docId);
              })
              .catch((err: Error) => {
                updateUpload(uploadId, {
                  phase: "error",
                  errorMessage: err.message || "Failed to finalize upload",
                });
              });
          });

          xhr.open("PUT", uploadUrl, true);
          xhr.setRequestHeader("Content-Type", fileMimeType);
          xhr.send(file);
        })
        .catch((err: Error) => {
          updateUpload(uploadId, {
            phase: "error",
            errorMessage: err.message || "Failed to initiate upload",
          });
        });
    },
    [createUpload, finalizeUpload, pollForStatus, updateUpload]
  );

  const executeRecordingUpload = useCallback(
    (uploadId: string, callId: string, interviewerId: string) => {
      updateUpload(uploadId, { phase: "processing" });

      createFromRecording({
        variables: { aiInterviewerId: interviewerId, callId },
      })
        .then(({ data, errors }) => {
          if (errors?.length || !data?.createDocumentFromRecording?.document) {
            throw new Error(
              errors?.[0]?.message || "Failed to create document from recording"
            );
          }

          const doc = data.createDocumentFromRecording.document;
          let extra: Record<string, unknown> | null = null;
          if (doc.extra) {
            try {
              extra =
                typeof doc.extra === "string"
                  ? (JSON.parse(doc.extra) as Record<string, unknown>)
                  : (doc.extra as Record<string, unknown>);
            } catch {
              extra = null;
            }
          }
          const analysis = parseAnalysisFromExtra(extra);

          setUploads((prev) => {
            const updated = prev.map((u) =>
              u.id === uploadId
                ? {
                    ...u,
                    phase: "complete" as const,
                    documentId: doc.id,
                    fileSizeBytes: doc.fileSizeBytes ?? 0,
                    analysis,
                  }
                : u
            );
            const completed = updated.find((u) => u.id === uploadId);
            if (completed) {
              onUploadComplete?.(completed);
            }
            return updated;
          });
        })
        .catch((err: Error) => {
          updateUpload(uploadId, {
            phase: "error",
            errorMessage: err.message || "Failed to process recording",
          });
        });
    },
    [createFromRecording, updateUpload, onUploadComplete]
  );

  // Flush pending uploads when aiInterviewerId becomes available
  useEffect(() => {
    if (!aiInterviewerId) return;

    const pendingFiles = new Map(pendingFilesRef.current);
    if (pendingFiles.size > 0) {
      pendingFilesRef.current.clear();
      pendingFiles.forEach((file, uploadId) => {
        updateUpload(uploadId, { phase: "uploading", uploadProgress: 0 });
        executeUpload(uploadId, file, aiInterviewerId);
      });
    }

    const pendingRecordings = new Map(pendingRecordingsRef.current);
    if (pendingRecordings.size > 0) {
      pendingRecordingsRef.current.clear();
      pendingRecordings.forEach(({ callId }, uploadId) => {
        executeRecordingUpload(uploadId, callId, aiInterviewerId);
      });
    }
  }, [aiInterviewerId, executeUpload, executeRecordingUpload, updateUpload]);

  useEffect(() => {
    return () => {
      xhrsRef.current.forEach((xhr) => xhr.abort());
      pollTimersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const startUpload = useCallback(
    (file: File): string => {
      const uploadId = crypto.randomUUID();
      const fileName = file.name;
      const fileMimeType = inferMimeType(file);
      const fileSizeBytes = file.size;

      const newUpload: DocumentUploadState = {
        id: uploadId,
        phase: aiInterviewerId ? "uploading" : "idle",
        fileName,
        fileMimeType,
        fileSizeBytes,
        uploadProgress: 0,
        documentId: null,
        analysis: null,
        errorMessage: null,
      };

      setUploads((prev) => [...prev, newUpload]);

      if (!aiInterviewerId) {
        pendingFilesRef.current.set(uploadId, file);
        return uploadId;
      }

      executeUpload(uploadId, file, aiInterviewerId);
      return uploadId;
    },
    [aiInterviewerId, executeUpload]
  );

  const startRecordingUpload = useCallback(
    (
      callId: string,
      callName: string,
      positionTitle?: string | null
    ): string => {
      const uploadId = crypto.randomUUID();
      const parts = [callName, positionTitle].filter(Boolean).join(" - ");
      const fileName = `${parts || "recording"}.txt`;

      const newUpload: DocumentUploadState = {
        id: uploadId,
        phase: aiInterviewerId ? "processing" : "idle",
        fileName,
        fileMimeType: "text/plain",
        fileSizeBytes: 0,
        uploadProgress: 1,
        documentId: null,
        analysis: null,
        errorMessage: null,
      };

      setUploads((prev) => [...prev, newUpload]);

      if (!aiInterviewerId) {
        pendingRecordingsRef.current.set(uploadId, {
          callId,
          callName,
          positionTitle,
        });
        return uploadId;
      }

      executeRecordingUpload(uploadId, callId, aiInterviewerId);
      return uploadId;
    },
    [aiInterviewerId, executeRecordingUpload]
  );

  return { uploads, startUpload, startRecordingUpload, removeUpload };
}
